/**
 * The two charts above an item list (design: "Led AI Console.dc.html", the
 * ISSUES block's `showAging` pair): how old the open work is, and how long
 * the closed work took.
 *
 * ## At the artboard's own scale
 *
 * These were built a third smaller than the design draws them, on an earlier
 * instruction that they were "too huge" above a dense table. That has been
 * reversed (user, 2026-09-11: "make sure the graphics at the top actually
 * match the design"), so every number here is the artboard's: 22/24 padding,
 * a 34px band bar, 22px figures, 14px close tracks.
 *
 * ## The band bar FILTERS, which is why it says so
 *
 * The earlier build dropped the design's "Tap a band to filter" label and its
 * "Show them" link, because the console had no age filter and a bar that
 * invites a tap and does nothing is worse than a bar that says nothing. The
 * filter exists now — see `onBand` — so the labels are back and they are
 * true.
 *
 * It is LOCAL state, not a URL parameter, which is a deliberate departure
 * from the console's habit of putting filters in the query. Two reasons: the
 * design holds it in component state as well, and a band is a glance at the
 * chart rather than a place you would send somebody. The saved view and the
 * project scope — which are places — stay in the URL as before.
 *
 * ## The counts do not move when you filter
 *
 * The bar is folded from every open row, never from the filtered set, so
 * tapping a band narrows the table under it without renumbering the bar you
 * tapped. A chart whose own numbers change when you use it cannot be used to
 * decide where to look next.
 *
 * ## The numbers come from the rendered rows
 *
 * See `lib/itemFlow`. Nothing here reads the clock or the network: the fold
 * runs over the same rows the table below is about, so the chart and the
 * table cannot disagree.
 */

import { C } from "../tokens";
import type { ItemFlow, ItemFlowBucket } from "../lib/itemFlow";
import { AGING_CALLOUT_DAYS, CLOSE_WINDOW_DAYS } from "../lib/itemFlow";
import { META, plural } from "./chrome";

/**
 * The band colours, in `AGE_BANDS` order.
 *
 * Cool for the first two and warm after: the design's point is that a reader
 * should see where the mass sits without reading a single label. Two golds
 * rather than gold-then-orange keeps "late" one idea with two depths.
 */
const BAND_TONE = [C.calOutside, C.propLabel, C.amber, C.amber, C.red] as const;
/** The first two bands are pale enough to need ink on them; the rest are not. */
const BAND_INK = [C.ink, C.ink, C.surface, C.surface, C.surface] as const;

/** Close-bucket colours, in `CLOSE_BUCKETS` order: fast is green, a month is
 *  neutral, over a month is the warning. */
const CLOSE_TONE = [C.green, C.green, C.popNum, C.amber] as const;

const CARD: React.CSSProperties = {
  background: C.surface,
  border: `1px solid ${C.hair}`,
  borderRadius: 16,
  padding: "22px 24px",
  display: "flex",
  flexDirection: "column",
  gap: 18,
  boxShadow: `0 1px 2px ${C.softShadowLow}`,
  minWidth: 0,
};

/**
 * The last band with anything in it — where "Show them" goes.
 *
 * The oldest band is the one a reader asking "what has been sitting" wants
 * first, and it is guaranteed non-empty because the callout that offers this
 * link only renders when something is past the line.
 */
function oldestOccupiedBand(flow: ItemFlow): number {
  for (let index = flow.bands.length - 1; index >= 0; index--) {
    if (flow.bands[index]!.count > 0) return index;
  }
  return flow.bands.length - 1;
}

/** The design's gold underscore under one word of the headline. */
const UNDERLINE: React.CSSProperties = {
  boxShadow: `inset 0 -2px 0 ${C.gold}`,
};

export function ItemFlowCards({
  flow,
  noun,
  maxWidth,
  band,
  onBand,
}: {
  flow: ItemFlow;
  /** What one row is called here — "observation", "task", "punch item". */
  noun: string;
  /** Kept in step with the table's own cap so the two edges line up. */
  maxWidth: number;
  /** The age band the table is narrowed to, or `null` for all of them. */
  band: number | null;
  /** Toggle a band. `null` clears. */
  onBand: (next: number | null) => void;
}) {
  // Nothing open and nothing closed recently is not an empty chart, it is a
  // list with no story yet. Two cards of zeroes above it would be furniture.
  if (flow.openCount === 0 && flow.closed === null) return null;

  return (
    <div
      data-testid="item-flow"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 400px), 1fr))",
        gap: 20,
        alignItems: "start",
        width: "100%",
        maxWidth,
        margin: "0 auto 16px",
      }}
    >
      <AgeCard flow={flow} noun={noun} band={band} onBand={onBand} />
      {flow.closed && <CloseCard closed={flow.closed} />}
    </div>
  );
}

/**
 * The design's dismissible chip for the band currently filtering the table.
 *
 * It sits below the cards rather than inside one, because it is about the
 * TABLE — and because a reader who has scrolled the bar off the top still
 * needs to see that the list under them is narrowed, and still needs a way
 * out that is not "scroll back up and find the band you tapped".
 *
 * Rendered by the list, not by the card, so it can sit in the list's own
 * filter row beside whatever else is narrowing it.
 */
export function ItemFlowBandChip({
  flow,
  band,
  onClear,
}: {
  flow: ItemFlow;
  band: number | null;
  onClear: () => void;
}) {
  if (band === null) return null;
  const label = flow.bands[band]?.label;
  if (!label) return null;
  return (
    <button
      type="button"
      className="kb"
      data-testid="flow-band-chip"
      onClick={onClear}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        height: 32,
        padding: "0 10px 0 12px",
        borderRadius: 999,
        border: "none",
        background: C.propValue,
        color: C.surface,
        font: "inherit",
        fontSize: 14.95,
        fontWeight: 500,
        cursor: "pointer",
      }}
    >
      {`Age ${label}`}
      <span aria-hidden style={{ display: "grid", placeItems: "center", opacity: 0.7 }}>
        <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
          <path
            d="M2.5 2.5l7 7M9.5 2.5l-7 7"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
      </span>
    </button>
  );
}

function AgeCard({
  flow,
  noun,
  band,
  onBand,
}: {
  flow: ItemFlow;
  noun: string;
  band: number | null;
  onBand: (next: number | null) => void;
}) {
  // `flex` is the count, floored at 1, so an empty band keeps a visible sliver
  // rather than collapsing — a band that vanished would make the axis below it
  // lie about which column is which.
  const total = flow.openCount;
  return (
    <div style={CARD} data-testid="flow-age">
      <Headline meta="Tap a band to filter">
        {`${plural(total, noun, `${noun}s`)} open, by `}
        <span style={UNDERLINE}>age</span>.
      </Headline>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "flex", height: 34, borderRadius: 9, overflow: "hidden", gap: 2 }}>
          {flow.bands.map((entry, index) => {
            const on = band === null || band === index;
            // An empty band is DRAWN, not offered — the axis below has to line
            // up with it, but filtering to it would empty the table and the
            // list would then say "no punch items yet", which is a lie about
            // the workspace rather than a fact about the band. Same rule the
            // daily log's week strip applies to a day that has not happened.
            const pickable = entry.count > 0;
            return (
              <button
                key={entry.label}
                type="button"
                className="kb"
                data-testid="flow-band"
                data-band={entry.label}
                data-count={entry.count}
                data-active={band === index ? "true" : undefined}
                disabled={!pickable}
                aria-pressed={band === index}
                aria-label={`${entry.count} ${entry.label}`}
                onClick={() => onBand(band === index ? null : index)}
                style={{
                  flex: `${Math.max(entry.count, 1)} 1 0`,
                  minWidth: 52,
                  display: "grid",
                  placeItems: "center",
                  border: "none",
                  padding: 0,
                  font: "inherit",
                  cursor: pickable ? "pointer" : "default",
                  // The unselected bands DIM rather than disappear — the axis
                  // below still has to line up with them.
                  opacity: on ? 1 : 0.28,
                  background: entry.count ? BAND_TONE[index] : C.tileGlyph,
                  color: entry.count ? BAND_INK[index] : C.sub2,
                  fontSize: 14.95,
                  fontWeight: 700,
                  letterSpacing: "-0.01em",
                }}
              >
                {entry.count}
              </button>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: 2 }}>
          {flow.bands.map((entry, index) => {
            const on = band === null || band === index;
            return (
              <button
                key={entry.label}
                type="button"
                className="kb"
                data-testid="flow-band-label"
                disabled={entry.count === 0}
                onClick={() => onBand(band === index ? null : index)}
                style={{
                  flex: `${Math.max(entry.count, 1)} 1 0`,
                  minWidth: 52,
                  display: "block",
                  textAlign: "left",
                  background: "transparent",
                  border: "none",
                  padding: "2px 0 0",
                  font: "inherit",
                  cursor: entry.count > 0 ? "pointer" : "default",
                  // A rule ONLY under the band being filtered on. The earlier
                  // build drew one under every band in that band's own colour,
                  // which read as a second, thinner bar under the first.
                  borderTop: `2px solid ${band === index ? C.propValue : "transparent"}`,
                }}
              >
                <span
                  style={{
                    ...META,
                    fontSize: 10.93,
                    letterSpacing: "0.08em",
                    color: on ? C.tileMeta : C.calOutside,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "block",
                  }}
                >
                  {entry.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Only when something is actually past the line. On a healthy list the
          sentence would be "0 have sat more than 7 days", which is a true
          statement nobody needs a box drawn around. */}
      {flow.overLine.count > 0 && (
        <div
          data-testid="flow-callout"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            padding: "12px 14px",
            borderRadius: 12,
            background: C.inset,
            border: `1px solid ${C.tileGlyph}`,
          }}
        >
          <span
            style={{
              fontSize: 22,
              fontWeight: 700,
              letterSpacing: "-0.025em",
              lineHeight: 1,
              color: C.red,
            }}
          >
            {flow.overLine.count}
          </span>
          <span style={{ flex: 1, fontSize: 15.52, lineHeight: 1.4, color: C.tileMeta }}>
            {/* "are older than", not the design's "have sat more than": the
                bands measure age now, and "sat" is a claim about inactivity
                that this number does not make. */}
            {`${flow.overLine.count === 1 ? "is" : "are"} older than ${AGING_CALLOUT_DAYS} days — ${flow.overLine.pct}% of everything open. The oldest is ${plural(flow.oldestDays, "day", "days")}.`}
          </span>
          {/*
            The design's "Show them". Its own handler selects ONE band (the
            15–30d one), which does not show the group the sentence beside it
            just counted — so this selects the oldest band that actually has
            something in it, and the sentence and the link agree.
          */}
          <button
            type="button"
            className="kb"
            data-testid="flow-show-them"
            onClick={() => onBand(oldestOccupiedBand(flow))}
            style={{
              flex: "none",
              border: "none",
              background: "transparent",
              padding: 0,
              font: "inherit",
              fontSize: 14.37,
              fontWeight: 600,
              color: C.goldInk,
              whiteSpace: "nowrap",
              cursor: "pointer",
            }}
          >
            Show them
          </button>
        </div>
      )}
    </div>
  );
}

function CloseCard({ closed }: { closed: NonNullable<ItemFlow["closed"]> }) {
  return (
    <div style={CARD} data-testid="flow-close">
      <Headline meta="Time to close">
        {`${closed.count} `}
        <span style={UNDERLINE}>closed out</span>
        {` in ${CLOSE_WINDOW_DAYS} days.`}
      </Headline>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {closed.buckets.map((bucket, index) => (
          <CloseRow
            key={bucket.label}
            bucket={bucket}
            total={closed.count}
            tone={CLOSE_TONE[index]!}
          />
        ))}
      </div>

      <div
        style={{
          display: "flex",
          gap: 26,
          flexWrap: "wrap",
          paddingTop: 4,
          borderTop: `1px solid ${C.tileGlyph}`,
        }}
      >
        <Figure value={plural(closed.medianDays, "day", "days")} label="typical close" />
        <Figure value={`${closed.withinWeekPct}%`} label="closed within a week" tone={C.green} />
        {closed.fastest && (
          <Figure
            value={`${closed.fastest.owner} · ${closed.fastest.days}d`}
            label="fastest owner"
          />
        )}
      </div>
    </div>
  );
}

function CloseRow({
  bucket,
  total,
  tone,
}: {
  bucket: ItemFlowBucket;
  total: number;
  tone: string;
}) {
  return (
    <div
      data-testid="flow-close-row"
      data-bucket={bucket.label}
      data-count={bucket.count}
      style={{
        display: "grid",
        /* 76px, not 64: "> 1 Month" is the longest label and at 64 it
           overlapped its own bar. The design's own column is 76. */
        gridTemplateColumns: "76px minmax(0, 1fr) 34px",
        gap: 12,
        alignItems: "center",
      }}
    >
      <span
        style={{
          ...META,
          fontSize: 11.5,
          letterSpacing: "0.06em",
          color: C.tileMeta,
          whiteSpace: "nowrap",
        }}
      >
        {bucket.label}
      </span>
      <span
        style={{
          height: 14,
          borderRadius: 4,
          background: C.hairRow,
          overflow: "hidden",
        }}
      >
        <span
          style={{
            display: "block",
            height: "100%",
            borderRadius: 4,
            background: tone,
            width: `${total === 0 ? 0 : Math.round((bucket.count / total) * 100)}%`,
          }}
        />
      </span>
      <span style={{ fontSize: 14.95, fontWeight: 600, textAlign: "right" }}>{bucket.count}</span>
    </div>
  );
}

function Headline({ children, meta }: { children: React.ReactNode; meta?: string }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
      <span
        style={{
          fontSize: 19.55,
          fontWeight: 600,
          letterSpacing: "-0.015em",
          whiteSpace: "nowrap",
        }}
      >
        {children}
      </span>
      {meta && (
        <span style={{ ...META, fontSize: 10.93, letterSpacing: "0.1em", color: C.filterLabel }}>
          {meta}
        </span>
      )}
    </div>
  );
}

function Figure({ value, label, tone }: { value: string; label: string; tone?: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3, paddingTop: 12, minWidth: 0 }}>
      <span
        style={{
          fontSize: 22,
          fontWeight: 700,
          letterSpacing: "-0.025em",
          lineHeight: 1,
          color: tone ?? C.ink,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {value}
      </span>
      <span
        style={{ ...META, fontSize: 10.93, letterSpacing: "0.1em", color: C.filterLabel, whiteSpace: "nowrap" }}
      >
        {label}
      </span>
    </div>
  );
}
